// Linha da lista de conversas (pane esquerda do Inbox). Renderiza avatar + nome,
// preview da última msg, hora relativa, unread badge e status badge.
// SRP: só apresentação — clique notifica via onSelect.
import { memo } from 'react';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils/cn';
import type { ConversationStatus, ConversationSummary } from '../api';
import { formatChatTime } from '../utils/time';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { isWindowExpired } from '@/lib/utils/whatsappWindow';
import styles from './ConversationListItem.module.css';

interface Props {
  conversation: ConversationSummary;
  active: boolean;
  onSelect: (id: string) => void;
  /** Nº de mensagens do cliente não lidas (vem da slice notifications, ao vivo). */
  unread?: number;
}

const STATUS_BADGE: Record<
  ConversationStatus,
  { variant: React.ComponentProps<typeof Badge>['variant']; label: string }
> = {
  IA: { variant: 'purple', label: 'IA' },
  QUEUE: { variant: 'warning', label: 'Aguardando atendimento' },
  ASSIGNED: { variant: 'warning', label: 'Aguardando atendimento' },
  ACTIVE: { variant: 'success', label: 'Em atendimento' },
  DONE: { variant: 'gray', label: 'Concluída' },
};

function ConversationListItemBase({ conversation, active, onSelect, unread = 0 }: Props) {
  /**
   * Conversa vinda do onboarding e já fora da janela mostra a ORIGEM, não um status de
   * atendimento. Depois de importar o histórico, dezenas delas apareciam como "Aguardando
   * atendimento" — um atendimento que nunca existiu e que ninguém pode fazer, porque a
   * Meta recusa resposta fora das 24h.
   *
   * ⚠️ Só as vencidas. A importada em que o cliente falou nas últimas 24h continua
   * "Aguardando atendimento": ali há trabalho de verdade, e esconder isso recriaria o
   * defeito que deixou o histórico recente invisível.
   */
  const doOnboarding = conversation.importado === true && isWindowExpired(conversation);
  const meta = doOnboarding
    ? ({ variant: 'gray', label: 'Onboarding' } as const)
    : STATUS_BADGE[conversation.status];
  const contact = conversation.contact;
  const lastTime = formatChatTime(conversation.lastMessageAt);
  // Janela de 24h vencida NÃO conclui a conversa — mas ela também não recebe mais
  // mensagem: o retorno do cliente abre conversa nova (RF-03). O que sobra é uma conversa
  // que só o atendente pode encerrar, então a badge pede exatamente isso. "Não atendido"
  // (ninguém respondeu dentro das 24h) prevalece — é o caso mais grave.
  const windowClosed = isWindowExpired(conversation);
  // Nada de "Não atendido"/"Pendente de fechamento" no que veio do onboarding: as duas
  // cobram uma ação sobre conversa que a loja já atendeu pelo celular, meses atrás.
  const attendanceBadge = doOnboarding ? null : conversation.unansweredAt ? (
    <Badge variant="danger">Não atendido</Badge>
  ) : windowClosed ? (
    <Badge variant="warning">Pendente de fechamento</Badge>
  ) : null;

  return (
    <button
      type="button"
      className={cn(styles.row, active && styles.active)}
      data-active={active || undefined}
      onClick={() => onSelect(conversation.id)}
      aria-current={active ? 'true' : undefined}
      aria-label={`Conversa com ${contact.name}, ${meta.label}, ${unread} mensagens não lidas`}
    >
      <Avatar name={contact.name} tone={contact.avatarTone} size="md" />
      <span className={styles.body}>
        <span className={styles.headerLine}>
          <strong className={styles.name}>{contact.name}</strong>
          <span className={styles.time}>{lastTime}</span>
        </span>
        <span className={styles.previewLine}>
          <span className={styles.preview}>
            {conversation.lastMessagePreview ?? 'Sem mensagens'}
          </span>
          {unread > 0 && (
            <span className={styles.unread} aria-label={`${unread} não lidas`}>
              {unread}
            </span>
          )}
        </span>
        <span className={styles.metaLine}>
          <Badge variant={meta.variant}>{meta.label}</Badge>
          {attendanceBadge}
          {/* MET-15: onde a conversa está no funil, sem abrir o painel. */}
          {conversation.stage && <Badge variant="info">{conversation.stage.label}</Badge>}
          {/* MET-25: quanto falta da janela de 24h. Sem `aria-live` — quem anuncia
              é o resumo da tela de tempo real, não N contadores numa lista. O
              ticker é compartilhado, então 50 itens usam UM setInterval. */}
          {!windowClosed && conversation.windowClosesAt && (
            <CountdownTimer
              to={conversation.windowClosesAt}
              expiredLabel="Janela expirada"
              srPrefix="Janela fecha em"
            />
          )}
          {conversation.status === 'ACTIVE' && conversation.assignedTo && (
            <span className={styles.assignee}>
              <Avatar
                name={conversation.assignedTo.name}
                tone={conversation.assignedTo.avatarTone}
                size="xs"
              />
              <span>{conversation.assignedTo.name.split(' ')[0]}</span>
            </span>
          )}
          {contact.company && <span className={styles.company}>{contact.company}</span>}
        </span>
      </span>
    </button>
  );
}

export const ConversationListItem = memo(ConversationListItemBase);
